import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button, IconButton } from '@/ui';
import { PresentationBusyContext } from '@/core/hooks';
import type { ConfirmOptions, Navigation, PresentOptions } from './contract';
import { activeSurfaceElement, useSurfaceElement } from './surface-chrome';

interface Presentation { id: number; content: ReactNode; options: PresentOptions; focus: HTMLElement | null; busy?: boolean; settle?: (result: boolean) => void }
interface Toast { id: number; message: string; tone: 'neutral' | 'success' | 'error' }
interface PresentationAPI extends Pick<Navigation, 'present' | 'confirm' | 'toast'> { dismissTop(): boolean; dismissForNavigation(): void; active: boolean }
const PresentationContext = createContext<PresentationAPI | null>(null);
export { usePresentationBusy } from '@/core/hooks';
export function usePresentations() {
  const value = useContext(PresentationContext);
  if (!value) throw new Error('Missing presentation provider');
  return value;
}

export function PresentationProvider({ children }: { children: ReactNode }) {
  const [sheets, setSheets] = useState<Presentation[]>([]);
  const [toast, setToast] = useState<Toast | null>(null);
  const current = useRef(sheets);
  const nextId = useRef(0);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const dismiss = useCallback((id: number, result = false) => {
    const sheet = current.current.find((item) => item.id === id);
    if (!sheet) return;
    const wasTop = current.current.at(-1)?.id === id;
    current.current = current.current.filter((item) => item.id !== id);
    const remainingTop = current.current.at(-1)?.id;
    setSheets(current.current);
    sheet.settle?.(result);
    sheet.options.onDismiss?.();
    requestAnimationFrame(() => {
      if (wasTop && current.current.at(-1)?.id === remainingTop && sheet.focus?.isConnected) sheet.focus.focus({ preventScroll: true });
    });
  }, []);
  const add = useCallback((content: ReactNode, options: PresentOptions = {}, settle?: Presentation['settle']) => {
    const id = ++nextId.current;
    const sheet: Presentation = { id, content, options, focus: document.activeElement instanceof HTMLElement ? document.activeElement : null, settle };
    // A late route/legal/user sheet waits below a nondismissible incoming/active call.
    const protectedIndex = options.dismissible === false ? -1 : current.current.findIndex(item => item.busy || item.options.dismissible === false);
    current.current = protectedIndex < 0 ? [...current.current, sheet]
      : [...current.current.slice(0, protectedIndex), sheet, ...current.current.slice(protectedIndex)];
    setSheets(current.current);
    return { dismiss: () => dismiss(id) };
  }, [dismiss]);
  const confirm = useCallback((options: ConfirmOptions) => new Promise<boolean>((settle) => {
    const id = nextId.current + 1;
    add(<Confirmation options={options} titleId={`shell-confirm-title-${id}`} onAnswer={(answer) => dismiss(id, answer)} />, { size: 'small' }, settle);
  }), [add, dismiss]);
  const showToast = useCallback<Navigation['toast']>((message, options) => {
    clearTimeout(toastTimer.current);
    setToast({ id: ++nextId.current, message, tone: options?.tone ?? 'neutral' });
    toastTimer.current = setTimeout(() => setToast(null), 4000);
  }, []);
  useEffect(() => () => {
    clearTimeout(toastTimer.current);
    for (const sheet of current.current) { sheet.settle?.(false); sheet.options.onDismiss?.(); }
    current.current = [];
  }, []);
  const dismissTop = useCallback(() => {
    const top = current.current.at(-1);
    if (!top) return false;
    if (!top.busy && top.options.dismissible !== false) dismiss(top.id);
    return true;
  }, [dismiss]);
  const dismissForNavigation = useCallback(() => {
    const removable = current.current.filter(sheet => !sheet.busy && sheet.options.dismissible !== false);
    if (!removable.length) return;
    current.current = current.current.filter(sheet => !removable.includes(sheet));
    setSheets(current.current);
    for (const sheet of removable.toReversed()) { sheet.settle?.(false); sheet.options.onDismiss?.(); }
  }, []);
  const setBusy = useCallback((id: number, busy: boolean) => {
    if (!current.current.some(sheet => sheet.id === id && !!sheet.busy !== busy)) return;
    current.current = current.current.map(sheet => sheet.id === id ? { ...sheet, busy } : sheet);
    setSheets(current.current);
  }, []);
  return <PresentationContext.Provider value={{ present: add, confirm, toast: showToast, dismissTop, dismissForNavigation, active: sheets.length > 0 }}>
    <div className="shell-presentation-content" inert={sheets.length > 0 || undefined}>{children}</div>
    {sheets.map((sheet, index) => <Sheet key={sheet.id} sheet={sheet} layer={index} setBusy={setBusy} top={index === sheets.length - 1} dismiss={() => dismiss(sheet.id)} />)}
    {toast && createPortal(<div key={toast.id} className="shell-toast" data-tone={toast.tone} role={toast.tone === 'error' ? 'alert' : 'status'} onClick={() => setToast(null)}>{toast.message}</div>, document.body)}
  </PresentationContext.Provider>;
}

function Confirmation({ options, titleId, onAnswer }: { options: ConfirmOptions; titleId: string; onAnswer: (answer: boolean) => void }) {
  const { t } = useTranslation('shell');
  return <div className="shell-confirmation">
    <h2 id={titleId}>{options.title}</h2>
    {options.message && <p>{options.message}</p>}
    <div className="shell-confirm-actions">
      <Button variant="tinted" className="shell-secondary" autoFocus data-autofocus onClick={() => onAnswer(false)}>{options.cancelLabel ?? t('cancel')}</Button>
      <Button variant={options.destructive ? 'destructive' : 'prominent'} className={options.destructive ? 'shell-destructive' : 'shell-primary'} onClick={() => onAnswer(true)}>{options.confirmLabel}</Button>
    </div>
  </div>;
}

const focusable = 'button:not(:disabled), a[href], input:not(:disabled), textarea:not(:disabled), select:not(:disabled), [tabindex]:not([tabindex="-1"])';

function Sheet({ sheet, layer, top, dismiss, setBusy }: { sheet: Presentation; layer: number; top: boolean; dismiss: () => void; setBusy(id: number, busy: boolean): void }) {
  const { t } = useTranslation('shell');
  const busyChanged = useCallback((busy: boolean) => setBusy(sheet.id, busy), [setBusy, sheet.id]);
  const panel = useRef<HTMLDivElement>(null);
  const body = useRef<HTMLDivElement>(null);
  const dismissControl = useCallback((root: HTMLElement) => {
    const labels = new Set([t('close'), t('cancel'), t('close', { ns: 'common' }), t('cancel', { ns: 'common' })]);
    return [...root.querySelectorAll<HTMLButtonElement>('header button')].find(button => activeSurfaceElement(root, button)
      && labels.has(button.getAttribute('aria-label') ?? button.textContent?.trim() ?? '')) ?? null;
  }, [t]);
  const ownedDismiss = useSurfaceElement(body, dismissControl);
  const dragStart = useRef<number | null>(null);
  const [expanded, setExpanded] = useState(false);
  const dismissRef = useRef(dismiss);
  dismissRef.current = dismiss;
  useEffect(() => {
    if (!top) return;
    const element = panel.current;
    const alreadyFocused = document.activeElement instanceof HTMLElement && element?.contains(document.activeElement) ? document.activeElement : undefined;
    const initial = element?.querySelector<HTMLElement>('[autofocus], [data-autofocus]') ?? alreadyFocused ?? [...element?.querySelectorAll<HTMLElement>(focusable) ?? []].find((node) => node.getClientRects().length > 0) ?? element;
    initial?.focus({ preventScroll: true });
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault(); event.stopImmediatePropagation();
        if (!sheet.busy && sheet.options.dismissible !== false) dismissRef.current();
      }
      if (event.key === 'Tab' && element) {
        const targets = [...element.querySelectorAll<HTMLElement>(focusable)].filter((node) => node.getClientRects().length > 0);
        const first = targets[0]; const last = targets.at(-1);
        if (!first) { event.preventDefault(); element.focus(); }
        else if (event.shiftKey && (document.activeElement === first || document.activeElement === element)) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    const onFocus = (event: FocusEvent) => {
      if (event.target instanceof Node && element && !element.contains(event.target)) element.focus({ preventScroll: true });
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('focusin', onFocus);
    return () => { document.removeEventListener('keydown', onKey, true); document.removeEventListener('focusin', onFocus); };
  }, [sheet.id, sheet.options.dismissible, sheet.busy, top, ownedDismiss]);
  return createPortal(<div className="shell-modal-backdrop" style={{ zIndex: 200 + layer }} inert={!top || undefined} onPointerDown={(event) => {
    if (event.target === event.currentTarget && !sheet.busy && sheet.options.dismissible !== false) dismiss();
  }}>
    <div ref={panel} className="shell-sheet" data-size={expanded ? 'large' : sheet.options.size ?? 'medium'} role="dialog" aria-modal={top} aria-labelledby={sheet.settle ? `shell-confirm-title-${sheet.id}` : undefined} aria-label={sheet.settle ? undefined : t('appName', { ns: 'common' })} tabIndex={-1}>
      <button className="shell-sheet-handle" aria-label={t('inspector')} onClick={() => setExpanded((value) => !value)} onPointerDown={(event) => { dragStart.current = event.clientY; event.currentTarget.setPointerCapture(event.pointerId); }} onPointerUp={(event) => {
        if (dragStart.current !== null) {
          const delta = event.clientY - dragStart.current;
          if (delta > 80 && !sheet.busy && sheet.options.dismissible !== false) dismiss();
          else if (delta < -35) setExpanded(true);
          dragStart.current = null;
        }
      }}><span /></button>
      {sheet.options.dismissible !== false && !ownedDismiss && <IconButton className="shell-icon-button shell-sheet-close" name="xmark" label={t('close')} disabled={sheet.busy} onClick={dismiss} />}
      <PresentationBusyContext.Provider value={busyChanged}><div className="shell-sheet-body" ref={body}>{sheet.content}</div></PresentationBusyContext.Provider>
    </div>
  </div>, document.body);
}
